import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Plus, Copy, Trash2, Ticket, Check } from "lucide-react";
import { toast } from "sonner";

const STATUS_CLS = {
  active: "bg-emerald-100 text-emerald-800 border-emerald-200",
  used: "bg-slate-100 text-slate-600 border-slate-200",
  expired: "bg-amber-100 text-amber-800 border-amber-200",
};

export function AdminInvitations() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [days, setDays] = useState("14");
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/invitations"); setItems(data); }
    catch (e) {} finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/admin/invitations", { email: email || null, expires_days: Number(days) });
      toast.success(`Invitation created: ${data.code}`);
      setEmail("");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setCreating(false); }
  };

  const revoke = async (code) => {
    try { await api.delete(`/admin/invitations/${code}`); toast.success("Invitation revoked"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const copy = (code) => {
    navigator.clipboard?.writeText(code);
    setCopied(code);
    toast.success("Code copied");
    setTimeout(() => setCopied(null), 1500);
  };

  return (
    <div className="space-y-6">
      <Card className="p-5 rounded-2xl border-border">
        <div className="flex items-center gap-2 mb-4 text-arctic-secondary font-heading font-semibold">
          <Ticket className="w-5 h-5 text-arctic-primary" /> Create an instructor invitation
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
          <div className="space-y-1.5 sm:col-span-1">
            <Label>Instructor email (optional)</Label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com" data-testid="invite-email" />
          </div>
          <div className="space-y-1.5">
            <Label>Expires in (days)</Label>
            <Input type="number" min="1" max="365" value={days} onChange={(e) => setDays(e.target.value)} data-testid="invite-days" />
          </div>
          <Button onClick={create} disabled={creating} data-testid="create-invite" className="rounded-full bg-arctic-primary hover:bg-arctic-primary/90">
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Generate code</>}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-3">If an email is set, only that address can redeem the code. Codes are single-use and expire.</p>
      </Card>

      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-arctic-primary" /></div>
      ) : (
        <Card className="rounded-2xl border-border overflow-hidden">
          <Table>
            <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Email</TableHead><TableHead>Status</TableHead><TableHead>Expires</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {items.map((inv) => (
                <TableRow key={inv.code} data-testid={`invite-row-${inv.code}`}>
                  <TableCell className="font-mono font-medium text-arctic-secondary">{inv.code}</TableCell>
                  <TableCell>{inv.email || <span className="text-muted-foreground">Any</span>}</TableCell>
                  <TableCell><span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold border capitalize ${STATUS_CLS[inv.status]}`}>{inv.status}</span></TableCell>
                  <TableCell className="text-sm text-muted-foreground">{inv.expires_at ? new Date(inv.expires_at).toLocaleDateString() : "—"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => copy(inv.code)} data-testid={`copy-invite-${inv.code}`} title="Copy code">
                        {copied === inv.code ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                      </Button>
                      {inv.status !== "used" && (
                        <Button variant="ghost" size="icon" onClick={() => revoke(inv.code)} data-testid={`revoke-invite-${inv.code}`} title="Revoke" className="text-muted-foreground hover:text-destructive">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No invitations yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
